import {
    useCallback,
    useState
} from "react";
import {resendSignUpCode} from "aws-amplify/auth";
import {pushAnalyticsEvent} from "@flexinet/analytics-components";
import {showNotification} from "@mantine/notifications";
import {
    AiOutlineExclamation,
    AiOutlineInfo
} from "react-icons/ai";

// FNP-878 — the escape hatch from a permanently stranded signup.
//
// Cognito's `signUp` for an address that is ALREADY registered but still
// UNCONFIRMED silently SUCCEEDS and sends nothing. So a user who closes the tab,
// loses the email, or lets the code expire cannot sign in (unconfirmed), cannot
// re-register (silent no-op) and — until this hook existed — could not ask for a
// new code either. The only recovery was an admin deleting the Cognito user.
//
// Evidence this is not hypothetical: six accounts sat UNCONFIRMED in the dev
// pool when this was written, the oldest (`tiggabad69@proton.me`) for nearly
// four months. That is a real person who tried to join and could not.
//
// ⚠️ THE POOL HAS `PreventUserExistenceErrors: ENABLED` — VERIFIED LIVE against
// client `flexinet-portal-app-client` on 2026-08-11. This is load-bearing for
// the error handling below, so do not "harden" it by reintroducing branches for
// cases this configuration makes unreachable:
//
//   * a NONEXISTENT user           -> SUCCEEDS, returns a plausible masked
//                                     destination (`d***@d***`). No exception.
//   * an ALREADY CONFIRMED user    -> SUCCEEDS, returns the masked destination.
//                                     No InvalidParameterException.
//
// Both were run against the live dev pool and both returned CodeDeliveryDetails.
// So `UserNotFoundException` and the already-confirmed `InvalidParameterException`
// CANNOT reach this catch through this client — handling them would be dead code
// asserting behaviour the platform does not exhibit.
//
// This also means the pool ALREADY delivers the privacy posture FNP-878 asked
// for ("do not disclose whether an address is registered"): Cognito refuses to
// distinguish the cases, at the source, rather than the UI papering over it.
// The consequence to accept is that a TYPO'd address reports success — the code
// simply goes nowhere. That is Cognito's deliberate trade, not ours to undo
// here.

export const useResendSignUpCode = () => {

    // Drives the button's loading/disabled state so a user cannot queue several
    // resends while the first is in flight — each request Cognito accepts counts
    // against the rate limit that produces LimitExceededException.
    const [resending, setResending] = useState(false);

    const resend = async (username: string) => {

        // The button is disabled without a valid email, so this guards a
        // programmatic call rather than an expected path. An empty username
        // would spend a rate-limit slot on a request that cannot succeed.
        if (!username) {
            return;
        }

        setResending(true);

        try {

            const {destination, deliveryMedium} = await resendSignUpCode({username});

            pushAnalyticsEvent('resend_sign_up_code')

            showNotification({
                                 title: 'Notification',
                                 icon: <AiOutlineInfo/>,
                                 // Cognito returns the MASKED destination
                                 // (`s***@d***`), so echoing it leaks nothing and
                                 // tells the user which mailbox to watch — the
                                 // common failure is checking the wrong inbox.
                                 message: destination
                                     ? `A new code was sent to ${destination}`
                                     : `A new code was sent by ${deliveryMedium ?? 'email'}`,
                                 color: 'green',
                             })

        } catch (e: unknown) {

            console.error(e);

            // ⚠️ Amplify v6 mints a FRESH Error object per failure, so this must
            // be matched on `name` — never by identity or against a cached
            // reference.
            const name = e instanceof Error ? e.name : '';

            if (name === 'LimitExceededException') {
                // The one error this call realistically produces. Cognito
                // rate-limits resends per user. A generic failure here reads as
                // "the site is broken" and the user retries, which makes it
                // worse — say plainly that waiting is the fix.
                showNotification({
                                     title: 'Too many attempts',
                                     icon: <AiOutlineExclamation/>,
                                     message: 'Too many codes requested. Please wait a few minutes before trying again.',
                                     color: 'orange',
                                 })
                return;
            }

            // Everything else surfaces its own message, matching the house style
            // of the sibling reset-password flow.
            showNotification({
                                 title: 'Error',
                                 icon: <AiOutlineExclamation/>,
                                 message: e instanceof Error ? e.message : 'An error occurred',
                                 color: 'red',
                             })

        } finally {
            setResending(false);
        }

    }

    const handleResend = useCallback(
        (username: string) => {
            return resend(username);
        },
        [],
    );

    return {
        resending,
        handleResend,
    };
};
